import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Colors, Display, ICbButtonProps, Sizes } from '../Button/Button';
import { Errors } from '../Inputs/InputWrapper';
import { Tooltip } from '../Tooltip/Tooltip';
export interface SingleSelectQuantityValueType {
	id: string;
	display: string;
	quantity: number;
}

export interface IMultiSelectQuantityValueType {
	values: SingleSelectQuantityValueType[];
}
export class MultiSelectQuantityValueType implements IMultiSelectQuantityValueType {

	public values: SingleSelectQuantityValueType[];

	constructor(initialValues: SingleSelectQuantityValueType[] = []) {
		this.values = [...initialValues];
	}

	// Return a copy to prevent mutation from outside
	get all(): SingleSelectQuantityValueType[] {
		return [...this.values];
	}

	getTotalQuantity(): number {
		const sum = this.values.map(x => x.quantity ?? 0).reduce((acc, curr) => acc + curr, 0);
		return sum;
	}

	getQuantity(id: string): number {
		return this.values.find(item => item.id === id)?.quantity ?? 0;
	}

	set(id: string, display: string, quantity: number) {
		this.values = this.values.filter(item => item.id !== id);
		if (quantity > 0) {
			this.values.push({
				id,
				display,
				quantity
			});
		}
	}

	remove(id: string) {
		this.values = this.values.filter(item => item.id !== id);
	}
}

export interface MultiSelectQuantityOption {
	id: string;
	label: string;
}

interface MultiSelectQuantityProps {
	value: MultiSelectQuantityValueType;
	onChange: (value: MultiSelectQuantityValueType) => void;
	options: MultiSelectQuantityOption[];
	// The input label
	label?: string | React.ReactNode;
	// The label to place above the top of the option section
	optionGroupLabel?: string;
	isRequired?: boolean;
	tooltip?: string | React.ReactNode;
	placeholder?: string;
	className?: string;
	errors?: string | string[]
	isDisabled?: boolean;
}

export const MultiSelectQuantity: React.FC<MultiSelectQuantityProps> = ({
	value,
	onChange,
	options,
	label,
	optionGroupLabel,
	isRequired = false,
	tooltip,
	className,
	errors,
	isDisabled = false,
	placeholder = "Select Quantities",
}) => {
	const [open, setOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const rootDivClassNames = classNames(
		'multi-select-quantity',
		'input-group',
		className,
		{
			'error': errors && errors.length > 0, // Add error class if there are errors
			'expanded': open, // Add expanded class when dropdown is open
			'required': isRequired, // Add required class if field is required
			'disabled': isDisabled, // Add disabled class if field is disabled
		}
	)

	const selectedItemDivClassNames = classNames("multi-select-quantity__selected-items", {
		'expanded': open,
		'error': errors && errors.length > 0, // Add error class if there are errors
	});

	const sortedOptions = options.sort((a, b) => a.label.localeCompare(b.label));

	const handleQuantityChange = (id: string, display: string, quantity: number) => {
		value.set(id, display, quantity);
		onChange(value);
	};

	const removeItem = (id: string) => {
		if (isDisabled) return;
		value.remove(id)
		onChange(value);
	};

	const renderSelectedItems = () => {
		if (value.all.length === 0) return <span className='placeholder'>{placeholder}</span>;

		const sortedAndMappedItems = value.all.map(item => {
			const optionData = options.find(option => option.id === item.id);
			return {
				...item,
				label: optionData ? optionData.label : item.id // Fallback to id if label not found
			};
		}).sort((a, b) => a.label.localeCompare(b.label));

		return (sortedAndMappedItems.map(item => (
			<div
				key={item.id}
				className="selected-item"
				onClick={e => {
					e.stopPropagation();
					removeItem(item.id);
				}}
			>
				<span>{item.label} ({item.quantity})</span><span className='cross-icon'> &#10005;</span>
			</div>
		)))
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleToggle = () => {
		if (!isDisabled) {
			setOpen(prev => !prev);
		}
	}

	return (
		<div className={rootDivClassNames} ref={dropdownRef}>
			{/* Label */}
			{label && (
				<div className="multi-select-quantity__label">
					{typeof label === 'string' ? <span className='label'>{label}</span> : label}
					{isRequired && <span className="required-indicator">*</span>}
				</div>
			)}
			{tooltip && (
				<div className='multi-select-quantity__tooltip'>
					{typeof tooltip === 'string' && <Tooltip id='multi-select-quantity-tooltip' content={tooltip} />}
				</div>
			)}
			{/* Errors */}
			<Errors errors={errors} />

			{/* Selected Pills or Placeholder */}
			<div className={selectedItemDivClassNames} onClick={handleToggle}>
				{renderSelectedItems()}
			</div>

			{/* Dropdown Options */}
			{open && (
				<div className="multi-select-quantity__options">
					{optionGroupLabel && (
						<div className="option-item">
							<span className='placeholder'>{optionGroupLabel}</span>
						</div>
					)}

					{sortedOptions.map(option => {
						const quantity = value.getQuantity(option.id);
						const commonBtnProps: Partial<ICbButtonProps> = {
							sizes: Sizes.Small,
							display: Display.Outline,
							colors: Colors.None
						}
						return (
							<div key={option.id} className="option-item">
								<span className='option-item__label'>{option.label}</span>
								<div className="option-item__actions">
									<Button
										className='decrement'
										icon={{ icon: 'minus', iconPos: 'icon-only' }}
										onClick={e => {
											e.stopPropagation();
											handleQuantityChange(option.id, option.label, quantity - 1);
										}}
										{...commonBtnProps}
									/>
									<span>{quantity || 0}</span>
									<Button
										className='increment'
										icon={{ icon: 'plus-2', 'iconPos': 'icon-only' }}
										onClick={e => {
											e.stopPropagation();
											handleQuantityChange(option.id, option.label, quantity + 1);
										}}
										{...commonBtnProps}
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};